import {
  type AdvancedFilterCondition,
  type AdvancedFiltersConfig,
} from '../types/config.types';

/**
 * A type-safe guard function to build advanced filter configurations against BE types
 * provided for an endpoint.
 *
 * @example
 * ```tsx
 * export type AdvancedFilterConditionDiscriminatedUnions =
 *   | AdvancedFilterCondition<'status', 'is' | 'is_not', 'active' | 'inactive'>
 *   | AdvancedFilterCondition<'created_at', 'before' | 'after', Date>
 *
 * export const mockInferredAdvancedFiltersConfig =
 *   createAdvancedFilterConfig<AdvancedFilterConditionDiscriminatedUnions>(
 *     {
 *       fieldOptions: {
 *         status: 'Status',
 *         created_at: 'Created At',
 *       },
 *       operatorOptions: {
 *         is: 'Is',
 *         is_not: 'Is Not',
 *         before: 'Before',
 *         after: 'After',
 *       },
 *       fieldConfigs: {
 *         status: {
 *           operators: ['is', 'is_not'],
 *           renderInput: (value, onChange) => (
 *             <TailwindSelect
 *               value={value}
 *               onValueChange={(val) => onChange(val as typeof value)}
 *             >
 *               <TailwindSelectTrigger>
 *                 <TailwindSelectValue placeholder={'Select a value'} />
 *               </TailwindSelectTrigger>
 *               <TailwindSelectContent data-testid="single-select-popover-content">
 *                 <TailwindSelectItem value="active">Active</TailwindSelectItem>
 *                 <TailwindSelectItem value="inactive">
 *                   Inactive
 *                 </TailwindSelectItem>
 *               </TailwindSelectContent>
 *             </TailwindSelect>
 *           ),
 *         },
 *         created_at: {
 *           operators: ['before', 'after'],
 *           renderInput: (value, onChange) => {
 *             return (
 *               <TailwindDatePicker
 *                 id={'created_at'}
 *                 date={value}
 *                 inputClassName="w-full h-12 rounded-md"
 *                 placeholderText={'pick a date'}
 *                 onChange={(val) => onChange(val)}
 *               />
 *             );
 *           },
 *         },
 *       },
 *     },
 *   );
 * ```
 */
export const createAdvancedFilterConfig = <U extends AdvancedFilterCondition>(
  config: AdvancedFiltersConfig<U>,
) => config;
